import { useAuthState } from '@auth/hooks';
import {
  ApiUsageBillingSection,
  AuthenticationProvidersSection,
  ProviderApplicationsSection,
  SecurityGroupsSection,
} from '@communities/content-management/components';
import type { EntityType, WorkflowType } from '@communities/content-management/domain';
import { isCommunity } from '@communities/content-management/utils';
import { FEATURE_FLAGS } from '@core/domain';
import { useIsFeatureEnabled } from '@core/hooks';
import { withAppInsightsTracking } from '@core/logging/withAppInsightsTracking';
import { Section } from '@soteria/design-system-ve';

interface IdentityAccessTabProps {
  entityId: string;
  entityType: EntityType;
  workflowType: WorkflowType;
  isEntityAdmin: boolean;
}

function IdentityAccessTabComponent({ entityId, entityType, workflowType, isEntityAdmin }: IdentityAccessTabProps) {
  const { isUserSystemAdmin } = useAuthState();
  const isSecurityGroupsEnabled = useIsFeatureEnabled(FEATURE_FLAGS.SECURITY_GROUPS);
  const isAuthenticationProvidersEnabled = useIsFeatureEnabled(FEATURE_FLAGS.AUTHENTICATION_PROVIDERS);

  return (
    <>
      {/* Security Groups Section */}
      {isSecurityGroupsEnabled && (
        <Section spacing="medium">
          <SecurityGroupsSection entityId={entityId} entityType={entityType} isEntityAdmin={isEntityAdmin} />
        </Section>
      )}

      {/* Authentication Providers Section (Only for Communities) */}
      {isCommunity(entityType) && isAuthenticationProvidersEnabled && (
        <Section spacing="medium">
          <AuthenticationProvidersSection />
        </Section>
      )}

      {/* Provider Applications Section (Only for Communities) */}
      {isCommunity(entityType) && (
        <Section spacing="medium">
          <ProviderApplicationsSection communityId={entityId} isCommunityAdmin={isEntityAdmin} />
        </Section>
      )}

      {/* API Usage & Billing Section (Only for Communities) */}
      {isCommunity(entityType) && (
        <Section spacing="medium">
          <ApiUsageBillingSection communityId={entityId} isCommunityAdmin={isEntityAdmin} isSystemAdmin={isUserSystemAdmin} workflowType={workflowType} />
        </Section>
      )}
    </>
  );
}

export const IdentityAccessTab = withAppInsightsTracking(IdentityAccessTabComponent, 'IdentityAccessTab');
